import React, { useState, useEffect } from 'react'
import moment from 'moment-timezone'
import { useParams } from 'react-router-dom'
import { FormattedMessage, useIntl } from 'react-intl'
import messages from './messages'
import {
    Button,
    Card,
    Input,
    Heading,
    Paragraph,
    Icon,
    LoadingFull as Loading
} from '@arnsbogroup/react-voicearchive'
import { setTheme, applyTheme, themeForPartner } from 'themes/utils'
import { NEUTRAL_THEME } from 'themes'
import { runtime } from 'rest/runtime'

const PublicSchedulingPollPage = () => {
    const { formatMessage } = useIntl()
    const { schedulingPollHashId } = useParams()
    const [request, setRequest] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [participantName, setParticipantName] = useState('')
    const [participantEmail, setParticipantEmail] = useState('')
    const [votes, setVotes] = useState({}) // { timeSlotUid: 'available' | 'unavailable' | 'maybe' }
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [submitted, setSubmitted] = useState(false)

    useEffect(() => {
        loadRequest()
    }, [schedulingPollHashId])

    useEffect(() => {
        // The poll's theme is written to :root. Coordinators can open this page
        // inside their own app, so restore their own theme on the way out rather
        // than leaving the whole UI in the poll partner's colours. Re-applying is
        // what App.js does on mount — clearing the inline styles instead would
        // drop --color-warning, which the server rendered :root does not define.
        return () => {
            applyTheme(themeForPartner(runtime.currentUser?.get('partner')))
        }
    }, [])

    const loadRequest = async () => {
        setLoading(true)
        try {
            const response = await fetch(`/rest/v2/schedulingpoll/${schedulingPollHashId}`, {
                method: 'GET',
                headers: {
                    'X-Requested-With': 'XMLHttpRequest'
                }
            })

            const data = await response.json()

            if (data.success && data.data) {
                const initialVotes = {}
                if (data.data.timeSlots) {
                    data.data.timeSlots.forEach(slot => {
                        initialVotes[slot.uid] = 'unavailable'
                    })
                }

                if (!data.data.brand) {
                    // include_branding is off: no logo and no partner colours.
                    applyTheme(NEUTRAL_THEME)
                } else if (data.data.brand.theme) {
                    setTheme(data.data.brand.theme)
                } else {
                    applyTheme(themeForPartner(data.data.partner))
                }

                setRequest(data.data)
                setVotes(initialVotes)
                setLoading(false)
            } else {
                // Nothing loaded, so nothing is known about who owns the poll.
                applyTheme(NEUTRAL_THEME)
                throw new Error(data.errors ? data.errors.join(', ') : 'Failed to load poll')
            }
        } catch (error) {
            console.error('Error loading scheduling poll:', error)
            setError(error.message)
            setLoading(false)
        }
    }

    const handleVoteChange = (timeSlotUid, voteValue) => {
        setVotes({
            ...votes,
            [timeSlotUid]: voteValue
        })
    }

    const handleSubmit = async (e) => {
        e.preventDefault()

        if (!participantName.trim()) {
            toastr.error(formatMessage(messages.enterNameError))
            return
        }

        if (!participantEmail.trim() || !isValidEmail(participantEmail)) {
            toastr.error(formatMessage(messages.enterValidEmailError))
            return
        }

        setIsSubmitting(true)

        try {
            const votesArray = Object.entries(votes).map(([timeSlotUid, vote]) => ({
                timeSlotUid: parseInt(timeSlotUid),
                vote
            }))

            const response = await fetch(`/rest/v2/schedulingpoll/${request.hash_id}/response`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest'
                },
                body: JSON.stringify({
                    participantName: participantName.trim(),
                    participantEmail: participantEmail.trim(),
                    votes: votesArray
                })
            })

            const data = await response.json()

            if (data.success) {
                setSubmitted(true)
                setIsSubmitting(false)
                toastr.success(formatMessage(messages.responseSubmittedSuccess))
            } else {
                toastr.error(data.errors ? data.errors.join(', ') : formatMessage(messages.submitResponseError))
                setIsSubmitting(false)
            }
        } catch (error) {
            console.error('Error submitting response:', error)
            toastr.error(error.message || formatMessage(messages.submitResponseError))
            setIsSubmitting(false)
        }
    }

    const isValidEmail = (email) => {
        const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        return re.test(email)
    }

    if (loading) {
        return <Loading />
    }

    const renderWithBranding = (content) => {
        const hasBranding = request?.brand !== null && request?.brand !== undefined
        const logo = request?.asset_uri || request?.brand?.logo || 'https://voicearchive-branding.s3.eu-central-1.amazonaws.com/small-curve-white.svg'
        const brandName = request?.brand?.displayName || request?.brand?.name || 'VoiceArchive'

        return (
            <div className="min-h-screen bg-gray-50 w-full">
                {hasBranding && (
                    <div className="bg-primary p-4 mb-8">
                        <div className="max-w-3xl mx-auto flex items-center">
                            <img
                                src={logo}
                                alt={brandName}
                                className="h-10 object-contain mx-auto"
                                onError={(e) => {
                                    e.target.src = 'https://voicearchive-branding.s3.eu-central-1.amazonaws.com/small-curve-white.svg'
                                }}
                            />
                        </div>
                    </div>
                )}
                <div className="flex items-center justify-center px-4" style={hasBranding ? {} : { paddingTop: '2rem' }}>
                    {content}
                </div>
            </div>
        )
    }

    if (error || !request) {
        return renderWithBranding(
            <Card className="bg-white max-w-md w-full mx-4 p-6">
                <div className="bg-red-50 border border-red-200 rounded p-4">
                    <Heading level={3} className="text-lg font-bold text-red-800 mb-2">
                        <FormattedMessage {...messages.errorTitle} />
                    </Heading>
                    <Paragraph className="text-red-700 mb-0">
                        {error || <FormattedMessage {...messages.errorRequestNotFound} />}
                    </Paragraph>
                </div>
            </Card>
        )
    }

    if (request.status !== 'open') {
        return renderWithBranding(
            <Card className="bg-white max-w-md w-full mx-4 p-6">
                <div className="bg-yellow-50 border border-yellow-200 rounded p-4">
                    <Heading level={3} className="text-lg font-bold text-yellow-800 mb-2">
                        <FormattedMessage {...messages.pollClosedTitle} />
                    </Heading>
                    <Paragraph className="text-yellow-700 mb-0">
                        <FormattedMessage {...messages.pollClosedMessage} />
                    </Paragraph>
                </div>
            </Card>
        )
    }

    if (submitted) {
        return renderWithBranding(
            <Card className="bg-white max-w-md w-full mx-4 p-6">
                <div className="text-center">
                    <Icon icon={['fas', 'check-circle']} className="text-5xl text-positive mb-4" />
                    <Heading level={2} className="text-2xl font-bold mb-2">
                        <FormattedMessage {...messages.thankYouTitle} />
                    </Heading>
                    <Paragraph className="mb-4">
                        <FormattedMessage {...messages.availabilityRecorded} values={{ title: request.title }} />
                    </Paragraph>
                    <Paragraph className="text-sm text-gray-600 mb-0">
                        <FormattedMessage {...messages.emailNotification} />
                    </Paragraph>
                </div>
            </Card>
        )
    }

    const userTimezone = moment.tz.guess()

    const groupTimeSlotsByDate = () => {
        if (!request.timeSlots) return {}

        const grouped = {}

        request.timeSlots.forEach(slot => {
            const dateKey = moment.utc(slot.start_datetime).tz(userTimezone).format('YYYY-MM-DD')
            if (!grouped[dateKey]) {
                grouped[dateKey] = []
            }
            grouped[dateKey].push(slot)
        })

        Object.keys(grouped).forEach(dateKey => {
            grouped[dateKey].sort((a, b) => {
                return moment.utc(a.start_datetime).valueOf() - moment.utc(b.start_datetime).valueOf()
            })
        })

        const sortedGrouped = {}
        Object.keys(grouped).sort().forEach(dateKey => {
            sortedGrouped[dateKey] = grouped[dateKey]
        })

        return sortedGrouped
    }

    const groupedTimeSlots = groupTimeSlotsByDate()
    const sortedDateKeys = Object.keys(groupedTimeSlots)

    const hasBranding = request?.brand != null && request?.brand != undefined

    const getLogo = () => {
        if (!hasBranding) {
            return null
        }
        if (request.asset_uri) {
            return request.asset_uri
        }
        if (request.brand?.logo) {
            return request.brand.logo
        }

        return 'https://voicearchive-branding.s3.eu-central-1.amazonaws.com/small-curve-white.svg'
    }

    const logo = getLogo()
    const brandName = request.brand?.displayName || request.brand?.name || 'VoiceArchive'

    return (
        <div className="min-h-screen bg-gray-50 w-full">
            {hasBranding && (
                <div className="bg-primary p-4 mb-8">
                    <div className="max-w-3xl mx-auto flex items-center">
                        <img
                            src={logo}
                            alt={brandName}
                            className="h-10 object-contain mx-auto"
                            onError={(e) => {
                                e.target.src = 'https://voicearchive-branding.s3.eu-central-1.amazonaws.com/small-curve-white.svg'
                            }}
                        />
                    </div>
                </div>
            )}

            <div className="px-4 pb-16" style={hasBranding ? {} : { paddingTop: '2rem' }}>
            <div className="max-w-3xl mx-auto">
                <Card className="bg-white p-6 mb-6">
                    <Heading level={1} className="text-3xl font-bold mb-2">{request.title}</Heading>
                    {request.description && (
                        <Paragraph className="break-all text-gray-600">{request.description}</Paragraph>
                    )}
                    <Paragraph className="text-sm text-gray-500 mt-3 mb-0">
                        <FormattedMessage {...messages.timesShownIn} values={{ timezone: userTimezone }} />
                    </Paragraph>
                </Card>

                {request.responseCount > 0 && (
                    <div className="bg-blue-50 border border-blue-200 rounded p-4 mb-6">
                        <Paragraph className="text-sm text-blue-900 mb-0">
                            <strong><FormattedMessage {...messages.peopleResponded} values={{ count: request.responseCount }} /></strong>
                        </Paragraph>
                    </div>
                )}

                <Card className="bg-white p-6">
                    <form onSubmit={handleSubmit}>
                        <div className="mb-6">
                            <Heading level={2} className="text-xl font-bold mb-4">
                                <FormattedMessage {...messages.yourInformation} />
                            </Heading>
                            <div className="space-y-4">
                                <div>
                                    <Input
                                        label={formatMessage(messages.yourName)}
                                        type="text"
                                        value={participantName}
                                        onChange={(e) => setParticipantName(e.target.value)}
                                        placeholder={formatMessage(messages.namePlaceholder)}
                                        required
                                    />
                                </div>
                                <div>
                                    <Input
                                        label={formatMessage(messages.yourEmail)}
                                        type="email"
                                        value={participantEmail}
                                        onChange={(e) => setParticipantEmail(e.target.value)}
                                        placeholder={formatMessage(messages.emailPlaceholder)}
                                        required
                                    />
                                    <Paragraph className="text-xs text-gray-500 mt-1 mb-0">
                                        <FormattedMessage {...messages.emailNotificationHint} />
                                    </Paragraph>
                                </div>
                            </div>
                        </div>

                        <div className="mb-6">
                            <Heading level={2} className="text-xl font-bold mb-4">
                                <FormattedMessage {...messages.selectAvailability} />
                            </Heading>
                            <Paragraph className="text-sm text-gray-600 mb-4">
                                <FormattedMessage {...messages.selectAvailabilityHint} />
                            </Paragraph>

                            <div className="space-y-6">
                                {sortedDateKeys.map(dateKey => {
                                    const slots = groupedTimeSlots[dateKey]
                                    const firstSlot = slots[0]
                                    const dateLabel = moment.utc(firstSlot.start_datetime).tz(userTimezone).format('dddd, MMMM D, YYYY')

                                    return (
                                        <div key={dateKey}>
                                            <div className="mb-3 pb-2 border-b-2 border-gray-300">
                                                <Heading level={3} className="text-lg font-semibold">{dateLabel}</Heading>
                                            </div>

                                            <div className="space-y-3">
                                                {slots.map(slot => {
                                                    const counts = slot.voteCounts || { available: 0, unavailable: 0, maybe: 0 }

                                                    return (
                                                        <div key={slot.uid} className="border border-gray-200 rounded-lg p-4 ml-4">
                                                            <div className="mb-3">
                                                                <div className="font-medium text-gray-900">
                                                                    {moment.utc(slot.start_datetime).tz(userTimezone).format('h:mm A')} -{' '}
                                                                    {moment.utc(slot.end_datetime).tz(userTimezone).format('h:mm A z')}
                                                                </div>
                                                                {request.responseCount > 0 && (
                                                                    <div className="mt-2 text-xs text-gray-500">
                                                                        {counts.available > 0 && <span className="mr-3 text-green-600">{counts.available} <FormattedMessage {...messages.available} /></span>}
                                                                        {counts.maybe > 0 && <span className="mr-3 text-yellow-600">{counts.maybe} <FormattedMessage {...messages.maybe} /></span>}
                                                                        {counts.unavailable > 0 && <span className="text-red-600">{counts.unavailable} <FormattedMessage {...messages.unavailable} /></span>}
                                                                    </div>
                                                                )}
                                                            </div>

                                                            <div className="flex gap-2">
                                                                <Button
                                                                    variant={votes[slot.uid] === 'available' ? 'solid' : 'outline'}
                                                                    state="positive"
                                                                    size="sm"
                                                                    icon={['fas', 'check']}
                                                                    onClick={() => handleVoteChange(slot.uid, 'available')}
                                                                >
                                                                    <FormattedMessage {...messages.available} />
                                                                </Button>
                                                                <Button
                                                                    variant={votes[slot.uid] === 'maybe' ? 'solid' : 'outline'}
                                                                    state="warning"
                                                                    size="sm"
                                                                    icon={['fas', 'question']}
                                                                    onClick={() => handleVoteChange(slot.uid, 'maybe')}
                                                                >
                                                                    <FormattedMessage {...messages.maybe} />
                                                                </Button>
                                                                <Button
                                                                    variant={votes[slot.uid] === 'unavailable' ? 'solid' : 'outline'}
                                                                    state="negative"
                                                                    size="sm"
                                                                    icon={['fas', 'times']}
                                                                    onClick={() => handleVoteChange(slot.uid, 'unavailable')}
                                                                >
                                                                    <FormattedMessage {...messages.unavailable} />
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    )
                                                })}
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        </div>

                        <div>
                            <Button
                                type="submit"
                                state="primary"
                                disabled={isSubmitting}
                                className="w-full"
                            >
                                {isSubmitting ? formatMessage(messages.submitting) : formatMessage(messages.submitAvailability)}
                            </Button>
                            <Paragraph className="text-xs text-gray-500 mt-2 text-center mb-0">
                                <FormattedMessage {...messages.updateResponseHint} />
                            </Paragraph>
                        </div>
                    </form>
                </Card>
            </div>
            </div>
        </div>
    )
}

export default PublicSchedulingPollPage
